import { Box, Button, Checkbox, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, IconButton, ListItemText, MenuItem, Select, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import generatePDF, { Resolution, Margin, Options } from 'react-to-pdf';
import { toast } from 'react-toastify';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import axios from '../../utils/axios';
import { Utils } from '../../utils/common-utils';

// The PDF is a screenshot of a DOM table, so it gets a lower ceiling than Excel.
const PDF_MAX_ROWS = 1000;
const PREVIEW_ROWS = 25;

// id → name for all the account's tags (inactive too, old records use them); never throws.
export const fetchTagNames = async (appliesTo: string): Promise<Record<string, string>> => {
  try {
    const response = await axios.get(`/api/v1/tag?appliesTo=${appliesTo}`);
    const names: Record<string, string> = {};
    (response.data || []).forEach((tag: any) => { if (tag?._id) names[tag._id] = tag.name; });
    return names;
  } catch (error) {
    console.error('Error fetching tags for report:', error);
    return {};
  }
};

/** Tags come back populated or as bare ids depending on the endpoint. */
export const tagNamesOf = (tags: any, tagNames: Record<string, string>): string[] =>
  (Array.isArray(tags) ? tags : [])
    .map((tag: any) => tag?.name || tagNames[tag?._id || tag])
    .filter(Boolean);

/** Location may be populated or a bare id. */
export const locationNameOf = (location: any, locations: any[]): string =>
  location?.name || (locations || []).find((l: any) => l._id === (location?._id || location))?.name || '';

interface ReportExportDialogProps {
  open: boolean;
  onClose: () => void;
  /** e.g. "Sales Report" — used for the dialog heading and the PDF heading. */
  title: string;
  loading: boolean;
  /** Flattened rows; keys are the column names. */
  rows: any[];
  /** Every column the report can carry, in display order. */
  columns: string[];
  /** Download file name, without extension. */
  fileName: () => string;
  sheetName: string;
  /** Extra line under the PDF heading (e.g. the date range). */
  pdfSubtitle?: string;
}

const formatCell = (value: any) => typeof value === 'number' ? Utils.formatNumber(value, true, true) : String(value ?? '-');

// Report preview for Sales / Purchase / Payments: pick columns, then download Excel or PDF.
const ReportExportDialog: FC<ReportExportDialogProps> = ({ open, onClose, title, loading, rows, columns, fileName, sheetName, pdfSubtitle }) => {
  const [selectedCols, setSelectedCols] = useState<string[]>(columns);
  const [isExporting, setIsExporting] = useState(false);
  const [pdfRows, setPdfRows] = useState<any[]>([]);

  // Every fresh open starts with all columns picked.
  useEffect(() => {
    if (open) setSelectedCols(columns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Column dropdown: the "All" entry toggles every column; picks always keep the original column order.
  const ALL_OPTION = '__all__';
  const handleColsChange = (value: string[]) => {
    if (value.includes(ALL_OPTION)) {
      setSelectedCols(selectedCols.length === columns.length ? [] : columns);
      return;
    }
    setSelectedCols(columns.filter((c) => value.includes(c)));
  };

  /** Keep only the picked columns, in the original order. */
  const pickCols = (row: any) => {
    const out: any = {};
    columns.forEach((c) => { if (selectedCols.includes(c)) out[c] = row[c]; });
    return out;
  };

  const handleExport = async (format: 'excel' | 'pdf') => {
    if (rows.length === 0) {
      toast.info('Nothing to download for the selected filters.');
      return;
    }
    if (selectedCols.length === 0) {
      toast.info('Select at least one column.');
      return;
    }
    setIsExporting(true);
    try {
      const picked = rows.map(pickCols);

      if (format === 'excel') {
        const worksheet = XLSX.utils.json_to_sheet(picked);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        saveAs(
          new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
          `${fileName()}.xlsx`
        );
      } else {
        const pdfOptions: Options = {
          filename: `${fileName()}.pdf`,
          method: 'save',
          resolution: Resolution.NORMAL,
          page: { margin: Margin.SMALL, format: 'A4', orientation: 'landscape' },
          canvas: { mimeType: 'image/jpeg', qualityRatio: 1 },
          overrides: { pdf: { compress: true }, canvas: { useCORS: true } }
        };
        // react-to-pdf paints a real DOM node, so render the rows off-screen first.
        setPdfRows(picked.slice(0, PDF_MAX_ROWS));
        await new Promise(resolve => setTimeout(resolve, 300));
        await generatePDF(() => document.getElementById('reportExportPdf'), pdfOptions);
        setPdfRows([]);

        if (picked.length > PDF_MAX_ROWS) {
          toast.warning(`The PDF holds the first ${PDF_MAX_ROWS} records. Use Excel for the full list.`);
        }
      }
      onClose();
    } catch (error) {
      console.error('Report export failed:', error);
      setPdfRows([]);
      toast.error('Could not download the report. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const downloadDisabled = isExporting || loading || rows.length === 0 || selectedCols.length === 0;

  return (
    <>
      <Dialog
        open={open}
        onClose={() => !isExporting && onClose()}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#f3f4f6', borderBottom: '1px solid #e5e7eb', py: 1.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}>{title} Preview</Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>
              {loading ? 'Loading records…' : `${rows.length} record(s) match the filters above`}
            </Typography>
          </Box>
          <IconButton
            size="small"
            disabled={isExporting}
            onClick={onClose}
            sx={{ color: '#374151', backgroundColor: '#e5e7eb', width: 34, height: 34, '&:hover': { backgroundColor: '#d1d5db' } }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Columns</Typography>
            <Select
              multiple
              size="small"
              displayEmpty
              value={selectedCols}
              onChange={(e) => handleColsChange(e.target.value as string[])}
              renderValue={(sel) => {
                const count = (sel as string[]).length;
                if (count === columns.length) return 'All columns';
                if (count === 0) return 'No columns';
                return `${count} of ${columns.length} columns`;
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 360,
                    // No green "selected" fill — the ticked checkbox already shows the pick.
                    '& .MuiMenuItem-root.Mui-selected': { backgroundColor: 'transparent' },
                    '& .MuiMenuItem-root.Mui-selected:hover, & .MuiMenuItem-root:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                  },
                },
              }}
              sx={{ minWidth: 220, fontSize: 13, borderRadius: '8px', backgroundColor: '#fff' }}
            >
              <MenuItem dense value={ALL_OPTION} sx={{ borderBottom: '1px solid #e5e7eb' }}>
                <Checkbox
                  size="small"
                  checked={selectedCols.length === columns.length}
                  indeterminate={selectedCols.length > 0 && selectedCols.length < columns.length}
                />
                <ListItemText primary="All" primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }} />
              </MenuItem>
              {columns.map((col) => (
                <MenuItem key={col} dense value={col}>
                  <Checkbox size="small" checked={selectedCols.includes(col)} />
                  <ListItemText primary={col} primaryTypographyProps={{ fontSize: 13 }} />
                </MenuItem>
              ))}
            </Select>

            {/* Downloads sit on the columns row, at its right end. */}
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              <Button
                onClick={() => handleExport('excel')}
                disabled={downloadDisabled}
                startIcon={<GridOnOutlinedIcon />}
                variant="contained"
                disableElevation
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', height: 36, backgroundColor: '#059669', '&:hover': { backgroundColor: '#047857' } }}
              >
                Download Excel
              </Button>
              <Button
                onClick={() => handleExport('pdf')}
                disabled={downloadDisabled}
                startIcon={isExporting ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <PictureAsPdfOutlinedIcon />}
                variant="contained"
                disableElevation
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', height: 36, backgroundColor: '#059669', '&:hover': { backgroundColor: '#047857' } }}
              >
                {isExporting ? 'Preparing…' : 'Download PDF'}
              </Button>
            </Box>
          </Box>
          <Divider sx={{ mb: 1.5 }} />
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress color="info" />
              <Typography sx={{ mt: 1.5, color: '#6b7280' }}>Fetching records for the selected filters…</Typography>
            </Box>
          ) : rows.length === 0 ? (
            <Typography sx={{ textAlign: 'center', py: 6, color: '#6b7280' }}>Nothing to download for the selected filters.</Typography>
          ) : selectedCols.length === 0 ? (
            <Typography sx={{ textAlign: 'center', py: 6, color: '#6b7280' }}>Select at least one column to preview.</Typography>
          ) : (
            <Box sx={{ overflow: 'auto', maxHeight: '60vh', border: '1px solid #e9ecf1', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    {selectedCols.map((header) => (
                      <th key={header} style={{ position: 'sticky', top: 0, border: '1px solid #e9ecf1', backgroundColor: '#f3f4f6', color: '#374151', padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, PREVIEW_ROWS).map((row: any, index: number) => (
                    <tr key={index} style={{ backgroundColor: index % 2 ? '#f9fafb' : '#fff' }}>
                      {selectedCols.map((header) => (
                        <td key={header} style={{ border: '1px solid #e9ecf1', padding: '6px 8px', color: '#111827', whiteSpace: 'nowrap' }}>
                          {formatCell(row[header])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > PREVIEW_ROWS && (
                <Typography sx={{ fontSize: '0.78rem', color: '#6b7280', p: 1, textAlign: 'center' }}>
                  Showing first {PREVIEW_ROWS} of {rows.length} records. The download includes all of them.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {pdfRows.length > 0 && (
        <div style={{ position: 'fixed', left: -10000, top: 0, width: 1400, backgroundColor: '#fff' }}>
          <div id='reportExportPdf' style={{ padding: 16, fontFamily: 'Arial, sans-serif' }}>
            <h2 style={{ margin: '0 0 4px 0', fontSize: 18 }}>{title}</h2>
            <p style={{ margin: '0 0 12px 0', fontSize: 11, color: '#666' }}>
              {pdfRows.length} record(s){pdfSubtitle ? ` · ${pdfSubtitle}` : ''} · generated {moment().format('DD MMM YYYY, hh:mm A')}
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
              <thead>
                <tr>
                  {Object.keys(pdfRows[0]).map((header) => (
                    <th key={header} style={{ border: '1px solid #dcdfe5', backgroundColor: '#488dff', color: '#fff', padding: 5, textAlign: 'left' }}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pdfRows.map((row: any, index: number) => (
                  <tr key={index} style={{ backgroundColor: index % 2 ? '#f7f9fc' : '#fff' }}>
                    {Object.keys(pdfRows[0]).map((header) => (
                      <td key={header} style={{ border: '1px solid #dcdfe5', padding: 5 }}>
                        {formatCell(row[header])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportExportDialog;
